import type { VendorRatingSummary } from "@shared/types";
import { Star } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

/**
 * A vendor's rating, always with what it measures: "4.6 · rated by 3 people
 * after use". A vendor nobody has rated reads "Not yet rated" -- there is
 * no number to show, and 0.0 would read as a score of zero.
 */
export function VendorRating({
	rating,
	className,
}: {
	rating: VendorRatingSummary;
	className?: string;
}): React.ReactElement {
	const { t } = useTranslation();

	if (rating.averageRating === null || rating.ratedByCount === 0) {
		return (
			<span className={cn("text-muted-foreground", className)}>
				{t("vendorRating.notYetRated")}
			</span>
		);
	}

	return (
		<span className={cn("inline-flex items-center gap-1", className)}>
			<Star
				className="h-3.5 w-3.5 fill-yellow-400 text-yellow-400"
				aria-hidden="true"
			/>
			{t("vendorRating.summary", {
				rating: rating.averageRating.toFixed(1),
				count: rating.ratedByCount,
			})}
		</span>
	);
}
